/**
 * The top-centre HUD status stack (H11, D-617 §6). One column of chips of one height and style under the top strip:
 * the Charter jurisdiction chip, gate line and toasts, the M2 toasts ("Game saved"), and the waiting-hail prompt.
 * Newest on top, at most `max` shown; beyond that the oldest unpinned chip hides (the hail is pinned: it never
 * times out and must stay answerable).
 *
 * The chips live in their own features' DOM (the e2e suite reads them there), so this module does not own a parent
 * element: every chip is absolutely placed at the stack's top (`.hud-chip` in `status.css`) and this coordinator
 * writes each one's slot as a `transform`. It runs only on show and hide events, never per frame, and writes a
 * transform only when a chip moves.
 */
import { z } from 'zod'
import statusJson from './status.json'
import './status.css'

/** A chip's fields the stack writes (an `HTMLElement` in the game, a plain object in tests). */
export interface StatusNode {
  readonly style: { transform: string }
  /** data-on and data-over go here when present (the M2 toasts keep their own data-on channel). */
  readonly dataset?: Record<string, string | undefined>
  /** False once the chip left the page (a disposed HUD); such chips drop out of the stack. */
  readonly isConnected?: boolean
}

const StatusConfigSchema = z.strictObject({
  comment: z.string().optional(),
  max: z.number().int().min(1).max(8),
  pitchPx: z.number().positive(),
})
export type StatusConfig = z.infer<typeof StatusConfigSchema>

export function loadStatusConfig(raw: unknown = statusJson): StatusConfig {
  return StatusConfigSchema.parse(raw)
}

export const STATUS_HIDDEN = 'scale(0)'

export interface StatusStack {
  readonly max: number
  /** Slot transforms, slot 0 (top) first. */
  readonly slots: readonly string[]
  /** Shown chips, newest first (overflowed ones included). */
  readonly shown: StatusNode[]
  readonly pinned: Set<StatusNode>
}

export function createStatusStack(cfg: StatusConfig = loadStatusConfig()): StatusStack {
  const slots: string[] = []
  for (let i = 0; i < cfg.max; i++) slots.push(i === 0 ? 'none' : `translateY(${i * cfg.pitchPx}px)`)
  return { max: cfg.max, slots, shown: [], pinned: new Set() }
}

export function statusSlotTransform(s: StatusStack, slot: number): string {
  return s.slots[slot]
}

/** The game's one stack, shared by the Charter HUD, the M2 HUD and the comms hail prompt. */
export const hudStatus: StatusStack = createStatusStack()

function write(n: StatusNode, transform: string, over: boolean): void {
  if (n.style.transform !== transform) n.style.transform = transform
  const o = over ? '1' : '0'
  if (n.dataset !== undefined && n.dataset.over !== o) n.dataset.over = o
}

function layout(s: StatusStack): void {
  for (let i = s.shown.length - 1; i >= 0; i--) {
    if (s.shown[i].isConnected === false) {
      s.pinned.delete(s.shown[i])
      s.shown.splice(i, 1)
    }
  }
  // Overflow: hide the oldest unpinned chips first, then (all pinned) the oldest pinned ones.
  let over = s.shown.length - s.max
  const hidden = new Set<StatusNode>()
  for (let i = s.shown.length - 1; i >= 0 && over > 0; i--) {
    if (!s.pinned.has(s.shown[i])) {
      hidden.add(s.shown[i])
      over--
    }
  }
  for (let i = s.shown.length - 1; i >= 0 && over > 0; i--) {
    if (!hidden.has(s.shown[i])) {
      hidden.add(s.shown[i])
      over--
    }
  }
  let slot = 0
  for (const n of s.shown) {
    if (hidden.has(n)) write(n, STATUS_HIDDEN, true)
    else write(n, s.slots[slot++], false)
  }
}

/** Shows a chip on top of the stack (or raises it there). Event-driven. */
export function statusShow(s: StatusStack, n: StatusNode, pinned = false): void {
  const i = s.shown.indexOf(n)
  if (i >= 0) s.shown.splice(i, 1)
  s.shown.unshift(n)
  if (pinned) s.pinned.add(n)
  else s.pinned.delete(n)
  if (n.dataset !== undefined && n.dataset.on !== '1') n.dataset.on = '1'
  layout(s)
}

/** Hides a chip and closes the gap it leaves. A chip not in the stack is left alone. */
export function statusHide(s: StatusStack, n: StatusNode): void {
  const i = s.shown.indexOf(n)
  if (i < 0) return
  s.shown.splice(i, 1)
  s.pinned.delete(n)
  if (n.dataset !== undefined) n.dataset.on = '0'
  write(n, STATUS_HIDDEN, false)
  layout(s)
}
